import { isAxiosError } from 'axios'

import { INTEGRATION_SERVICE, IntegrationService } from '../../types/misc'
import {
  NylasCalendarCompanyUserIntegrationItem,
  NylasCalendarIntegrationCollectionItem,
  NylasEmailIntegrationCollectionItem,
  NylasEmailIntegrationItem,
} from '../../types/responses/company-user-integrations'
import apiService from './apiService'

class CompanyUserIntegrationService {
  private endpoint = '/v1/company-user-integrations'

  public async deleteById(companyUserIntegrationId: number): Promise<void> {
    const response = await apiService.delete(
      `${this.endpoint}/${companyUserIntegrationId}`,
    )

    return response.data
  }

  private async getByIntegratedService(
    integratedService: IntegrationService,
  ): Promise<
    NylasEmailIntegrationItem | NylasCalendarCompanyUserIntegrationItem | null
  > {
    try {
      const response = await apiService.get(
        `${this.endpoint}/${integratedService}`,
      )

      return response.data
    } catch (error) {
      if (isAxiosError(error)) {
        // 404 is expected if there's no integration of this type
        if (error.status === 404) {
          return null
        }
      }

      throw error
    }
  }

  public async getNylasEmailIntegration(): Promise<NylasEmailIntegrationItem | null> {
    return this.getByIntegratedService(
      INTEGRATION_SERVICE.nylasEmail,
    ) as Promise<NylasEmailIntegrationItem | null>
  }

  public getNylasCalendarIntegration(): Promise<NylasCalendarCompanyUserIntegrationItem | null> {
    return this.getByIntegratedService(
      INTEGRATION_SERVICE.nylasCalendar,
    ) as Promise<NylasCalendarCompanyUserIntegrationItem | null>
  }

  private async getAllForCurrentCompanyByintegratedService(
    integratedService: IntegrationService,
  ): Promise<
    | NylasCalendarIntegrationCollectionItem
    | NylasEmailIntegrationCollectionItem
    | []
  > {
    try {
      const response = await apiService.get(
        `/v1/companies/company-user-integrations/${integratedService}`,
      )

      return response.data['hydra:member']
    } catch (e) {
      if (isAxiosError(e)) {
        // Forbidden is probably just because there aren't any
        if (e.status === 403) {
          return []
        }
      }

      throw e
    }
  }

  public async getAllNylasCalendarIntegrationsForCurrentCompany(): Promise<NylasCalendarIntegrationCollectionItem> {
    return this.getAllForCurrentCompanyByintegratedService(
      INTEGRATION_SERVICE.nylasCalendar,
    ) as Promise<NylasCalendarIntegrationCollectionItem>
  }

  public async getAllNylasEmailIntegrationsForCurrentCompany(): Promise<NylasEmailIntegrationCollectionItem> {
    return this.getAllForCurrentCompanyByintegratedService(
      INTEGRATION_SERVICE.nylasEmail,
    ) as Promise<NylasEmailIntegrationCollectionItem>
  }
}

const companyUserIntegrationService = new CompanyUserIntegrationService()

export default companyUserIntegrationService
